import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { TicketComment } from "../interfaces/Ticket";
import { bare } from "./axios";
import { getAccessToken, renew } from "./session";
import { jwtIsExpired } from "../utils/jwtUtils";

/**
 * Tempo real da thread de chamado (SPEC 006, fase 3).
 *
 * A thread REST continua sendo o dado; isto é entrega push por cima dela. Se o hub não conectar,
 * a tela funciona igual — só não atualiza sozinha. Nada aqui pode quebrar o detalhe do chamado.
 *
 * ⚠ Entra DESLIGADO no servidor (`Itsm:Realtime:Enabled=false`). Com a flag em `false` a rota
 * `/hubs/tickets` **não existe** e o handshake toma 404 — que é o comportamento esperado, tratado
 * em silêncio.
 */

/**
 * A URL do hub sai da MESMA variável da API, para não existirem dois lugares com o endereço do
 * backend. O `/api` é sufixo do path (`.../api`), e o hub fica na RAIZ (`/hubs/tickets`) — daí o
 * recorte do sufixo em vez de `new URL(...).origin`, que quebraria se um dia a API subisse sob um
 * subpath.
 */
export function urlDoHub(
  apiBase: string | undefined = process.env.REACT_APP_URL_API_NINA
): string | null {
  if (!apiBase) return null;
  return apiBase.replace(/\/api\/?$/i, "") + "/hubs/tickets";
}

/**
 * Token para o handshake, renovando quando já venceu.
 *
 * ⚠ Por que isto não é zelo: o servidor liga `CloseOnAuthenticationExpiration`, então a conexão cai
 * quando o JWT vence — e o JWT dura 30 min. Numa aba parada com o chamado aberto **nenhuma
 * requisição HTTP acontece**, logo nenhum 401, logo o interceptor do axios nunca renova. Sem este
 * cuidado o `withAutomaticReconnect` reconectaria lendo do localStorage o mesmo token vencido, em
 * laço, até desistir — e a tela ficaria muda sem nada no console que explicasse.
 */
async function tokenValido(): Promise<string> {
  const atual = getAccessToken();
  if (atual && !jwtIsExpired(atual)) return atual;

  const novo = await renew((body) => bare.post("/Auth/Refresh", body));
  // Sem token novo a sessão acabou de verdade. Devolver string vazia faz o handshake falhar com
  // 401, o SignalR desiste, e o `PrivateRoute` cuida do redirecionamento na próxima navegação —
  // não é aqui que se decide expulsar alguém da tela.
  return novo ?? "";
}

/**
 * Entra na thread de um chamado e chama <paramref name="aoComentar"/> a cada comentário público.
 *
 * Devolve a função de limpeza. Chamá-la é obrigatório — é ela que sai do grupo e fecha o socket.
 *
 * ⚠ O `Subscribe` do servidor RECUSA quando o login não alcança o chamado, e a mensagem é a mesma
 * para "não existe" e "é de outro cliente", de propósito. Não exiba esse texto cru: para quem está
 * com o chamado aberto na tela ele não faz sentido nenhum.
 */
export function subscribeToTicket(
  ticketId: number,
  aoComentar: (comentario: TicketComment) => void
): () => void {
  const url = urlDoHub();
  if (!url || !ticketId) return () => {};

  let conexao: HubConnection | null = null;
  let cancelado = false;

  const conectar = async () => {
    const c = new HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: tokenValido,
        // ⚠ Obrigatório: o CORS da API não permite credenciais, e isso é decisão (o NINA autentica
        // por bearer token, não por cookie). Com o padrão `true` o navegador bloqueia o negotiate.
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    c.on("ticketCommentAdded", (comentario: TicketComment) => {
      if (!cancelado) aoComentar(comentario);
    });

    try {
      await c.start();
      if (cancelado) {
        await c.stop();
        return;
      }
      await c.invoke("Subscribe", ticketId);
      conexao = c;
    } catch {
      // Silêncio de propósito: com a flag desligada no servidor isto é 404 em toda abertura de
      // chamado, e um erro no console por isso treinaria o time a ignorar o console.
      try {
        await c.stop();
      } catch {
        /* já estava caído */
      }
    }
  };

  void conectar();

  return () => {
    cancelado = true;
    const c = conexao;
    conexao = null;
    if (!c) return;
    // `Unsubscribe` é cortesia — o `stop()` já tira a conexão de todos os grupos. Mas ele é barato
    // e deixa o estado do servidor limpo quando a conexão é reusada.
    c.invoke("Unsubscribe", ticketId)
      .catch(() => undefined)
      .finally(() => {
        void c.stop().catch(() => undefined);
      });
  };
}
